import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map, tap, retry } from 'rxjs/operators';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { environment } from '../../environments/environment';

export interface VideoGenerationParams {
  prompt: string;
  video_length: number;
  resolution: string;
  num_inference_steps: number;
  guidance_scale: number;
  flow_shift: number;
  embedded_guidance_scale: number;
}

export interface VideoGenerationResponse {
  downloadUrl: string;
  filename: string;
  result: string;
  success: boolean;
  videoPath: string;
}

@Injectable({
  providedIn: 'root'
})
export class ColabVideoService {
  private readonly API_URL = environment.API_URL;
  private readonly STORAGE_KEY = 'colab_video_url';
  private readonly backendUrl = 'http://localhost:5001';

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer
  ) {}

  getStorageKey(): string {
    return this.STORAGE_KEY;
  }

  getColabUrl(): Observable<string | null> {
    const url = localStorage.getItem(this.STORAGE_KEY);
    return of(url);
  }

  setColabUrl(url: string): Observable<void> {
    localStorage.setItem(this.STORAGE_KEY, url);
    return of(void 0);
  }

  generateVideo(params: VideoGenerationParams): Observable<VideoGenerationResponse> {
    const url = localStorage.getItem(this.STORAGE_KEY);
    if (!url) {
      throw new Error('Colab URL not set');
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-Colab-URL': url
    });

    return this.http.post<VideoGenerationResponse>(`${this.backendUrl}/api/generate-video`, params, { headers }).pipe(
      tap(response => console.log('Backend Response:', response)),
      catchError((error: HttpErrorResponse) => {
        console.error('Error generating video:', error);
        
        if (error.error instanceof ErrorEvent) {
          return throwError(() => new Error(`Client-side error: ${error.error.message}`));
        } else {
          let errorMessage = `Server error: ${error.status} ${error.statusText}`;
          if (error.error && typeof error.error === 'object' && error.error.error) {
            errorMessage += ` - ${error.error.error}`;
          }
          return throwError(() => new Error(errorMessage));
        }
      })
    );
  }

  downloadVideo(url: string): Observable<Blob> {
    return this.http.get(url, { responseType: 'blob' });
  }

  estimateGenerationTime(params: VideoGenerationParams): number {
    // Base time in seconds
    let baseTime = 30;

    // Adjust based on resolution
    const [width, height] = params.resolution.split('x').map(Number);
    const pixels = width * height;
    const basePixels = 848 * 480; // Base resolution (848x480)
    const resolutionMultiplier = Math.sqrt(pixels / basePixels);

    // Adjust based on video length
    const lengthMultiplier = params.video_length / 32;

    // Adjust based on inference steps
    const stepsMultiplier = params.num_inference_steps / 50;

    return Math.round(baseTime * resolutionMultiplier * lengthMultiplier * stepsMultiplier);
  }
} 